import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { LANGUAGE_NAMES } from '@b4b/shared';
import { useAdminUser } from '../api/queries.js';
import { formatDecimal } from '../components/admin/format.js';
import StackedBars from '../components/admin/StackedBars.js';

export default function AdminUser() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const detail = useAdminUser(id);
  const fmt = (iso: string | null) => (iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : t('admin.users.never'));

  return (
    <main className="mx-auto max-w-3xl space-y-4 px-4 py-4">
      <Link to="/admin" className="inline-flex min-h-[44px] items-center text-sm text-accent">← {t('admin.users.detail.back')}</Link>
      {detail.isPending ? <p className="text-neutral-500">{t('common.loading')}</p> : null}
      {detail.isError ? <p className="text-red-600">{t('admin.users.detail.notFound')}</p> : null}
      {detail.data ? (
        <>
          <header className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
            <h1 className="break-all text-lg font-bold">{detail.data.user.email}</h1>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <div><dt className="text-xs text-neutral-500">{t('admin.users.col.created')}</dt><dd>{fmt(detail.data.user.createdAt)}</dd></div>
              <div><dt className="text-xs text-neutral-500">{t('admin.users.col.lastActive')}</dt><dd>{fmt(detail.data.user.lastActiveAt)}</dd></div>
              <div><dt className="text-xs text-neutral-500">{t('admin.users.col.premium')}</dt><dd>{detail.data.user.premiumUntil ? fmt(detail.data.user.premiumUntil) : t('admin.users.noPremium')}</dd></div>
              <div><dt className="text-xs text-neutral-500">{t('admin.users.col.devices')}</dt><dd>{detail.data.user.devices}</dd></div>
              <div><dt className="text-xs text-neutral-500">{t('admin.users.readsToday')}</dt><dd>{detail.data.user.readsToday}</dd></div>
              <div><dt className="text-xs text-neutral-500">{t('admin.users.readsAvg')}</dt><dd>{formatDecimal(detail.data.user.readsPerDay30, i18n.language)}</dd></div>
              <div><dt className="text-xs text-neutral-500">{t('admin.users.col.saved')}</dt><dd>{detail.data.user.saved}</dd></div>
              <div><dt className="text-xs text-neutral-500">{t('admin.users.detail.mutes')}</dt><dd>{detail.data.mutes}</dd></div>
            </dl>
          </header>

          <section className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
            <h2 className="mb-2 font-semibold">{t('admin.users.detail.readsByDay')}</h2>
            <StackedBars
              labels={detail.data.readsByDay.map((d) => d.date)}
              series={[{ key: 'reads', label: t('admin.users.readsToday'), color: '#6366f1', values: detail.data.readsByDay.map((d) => d.reads) }]}
              ariaLabel={t('admin.users.detail.readsByDay')}
              height={120}
            />
          </section>

          <section className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
              <h2 className="mb-2 font-semibold">{t('admin.users.detail.categories')}</h2>
              <ul className="text-sm">
                {detail.data.categories.map((c) => (
                  <li key={c.category} className="flex justify-between"><span>{c.category || t('admin.users.detail.uncategorized')}</span><span className="tabular-nums text-neutral-500">{c.feeds}</span></li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
              <h2 className="mb-2 font-semibold">{t('admin.users.detail.settings')}</h2>
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between"><dt className="text-neutral-500">{t('admin.users.detail.language')}</dt><dd>{LANGUAGE_NAMES[detail.data.settings.language]}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500">{t('admin.users.detail.translateContent')}</dt><dd>{detail.data.settings.translateContent ? t('admin.users.detail.yes') : t('admin.users.detail.no')}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500">{t('admin.users.detail.notifications')}</dt><dd>{detail.data.settings.notificationsEnabled ? t('admin.users.detail.yes') : t('admin.users.detail.no')}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500">{t('admin.users.detail.feedLayout')}</dt><dd>{t(`feed.layout.${detail.data.settings.feedLayout}`)}</dd></div>
              </dl>
            </div>
          </section>
        </>
      ) : null}
    </main>
  );
}
